import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import { Toaster } from './components/ui/sonner';
import '@/App.css';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Entrepreneurs from './pages/Entrepreneurs';
import EntrepreneurDetail from './pages/EntrepreneurDetail';
import EntrepreneurLivelihoodsReport from './pages/EntrepreneurLivelihoodsReport';
import Staff from './pages/Staff';
import StaffDetail from './pages/StaffDetail';
import Loans from './pages/Loans';
import Invoices from './pages/Invoices';
import LoanDetail from './pages/LoanDetail';
import Expenditures from './pages/Expenditures';
import ExpenditureMasters from './pages/ExpenditureMasters';
import ResourcePersons from './pages/ResourcePersons';
import Funds from './pages/Funds';
import Reports from './pages/Reports';
import StatisticsReport from './pages/StatisticsReport';
import ActivityLogs from './pages/ActivityLogs';
import LoanMasters from './pages/LoanMasters';
import SearchPage from './pages/SearchPage';
import Branches from './pages/Branches';
import UserManagement from './pages/UserManagement';
import Assets from './pages/Assets';
import Layout from './components/Layout';

// Individual Report Pages
import CreditHistorySearch from './pages/CreditHistorySearch';
import CashbookReport from './pages/reports/CashbookReport';
import CreditReportPage from './pages/reports/CreditReportPage';
import LoanEmiReport from './pages/reports/LoanEmiReport';
import LoanLedgerReport from './pages/reports/LoanLedgerReport';
import DcbReport from './pages/reports/DcbReport';
import ParReport from './pages/reports/ParReport';
import RepaymentNpaReport from './pages/reports/RepaymentNpaReport';
import CrpEpReport from './pages/reports/CrpEpReport';
import AssetRegisterReport from './pages/reports/AssetRegisterReport';
import BranchComparisonReport from './pages/reports/BranchComparisonReport';
import GroundedEnterprisesReport from './pages/reports/GroundedEnterprisesReport';
import LoanCertificatesReport from './pages/reports/LoanCertificatesReport';
import FinancialSummaryReport from './pages/reports/FinancialSummaryReport';
import LivelihoodsReport from './pages/reports/LivelihoodsReport';
import MemberLedgerReport from './pages/reports/MemberLedgerReport';
import AllEntrepreneursReport from './pages/reports/AllEntrepreneursReport';
import ProjectExpenditureReport from './pages/reports/ProjectExpenditureReport';
import BusinessPlans from './pages/BusinessPlans';
import BusinessPlanForm from './pages/BusinessPlanForm';
import BusinessPlanReport from './pages/reports/BusinessPlanReport';
import LoanApplicationReport from './pages/reports/LoanApplicationReport';
import CashTransfers from './pages/CashTransfers';
import BackupRestore from './pages/BackupRestore';
import Requisitions from './pages/Requisitions';
import FinancialYears from './pages/FinancialYears';
import Livelihoods from './pages/Livelihoods';
import Notices from './pages/Notices';
import MemberLedger from './pages/MemberLedger';
import Projects from './pages/Projects';
import BranchLedger from './pages/BranchLedger';
import AllLedgersReport from './pages/reports/AllLedgersReport';
import CentreDetail from './pages/CentreDetail';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return children;
};

// Restrict CRP-EP role to ONLY Business Plans + Entrepreneurs (so they can enrol entrepreneurs themselves)
const CRPEPGuard = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const allowed = ['/business-plans', '/entrepreneurs'];
  if (user?.role === 'CRP-EP' && !allowed.some(p => location.pathname.startsWith(p))) {
    return <Navigate to="/business-plans" replace />;
  }
  return children;
};

// Admin-only route guard — used for sensitive modules (Member Ledger, Loan Masters, Livelihoods, etc.)
const AdminOnly = ({ children }) => {
  const { user } = useAuth();
  if (user?.role !== 'Admin') {
    return <Navigate to="/" replace />;
  }
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      
      <Route path="/" element={
        <ProtectedRoute>
          <CRPEPGuard><Layout /></CRPEPGuard>
        </ProtectedRoute>
      }>
        <Route index element={<Dashboard />} />
        <Route path="branches" element={<Branches />} />
        <Route path="users" element={<UserManagement />} />
        <Route path="entrepreneurs" element={<Entrepreneurs />} />
        <Route path="entrepreneurs/:entrepreneurId" element={<EntrepreneurDetail />} />
        <Route path="entrepreneurs/:entrepreneurId/livelihoods" element={<EntrepreneurLivelihoodsReport />} />
        <Route path="projects" element={<AdminOnly><Projects /></AdminOnly>} />
        <Route path="centres/:centreId" element={<CentreDetail />} />
        <Route path="branch-ledger" element={<BranchLedger />} />
        <Route path="reports/all-ledgers" element={<AdminOnly><AllLedgersReport /></AdminOnly>} />
        <Route path="staff" element={<Staff />} />
        <Route path="staff/:staffId" element={<StaffDetail />} />
        <Route path="loan-masters" element={<AdminOnly><LoanMasters /></AdminOnly>} />
        <Route path="loans" element={<Loans />} />
        <Route path="invoices" element={<Invoices />} />
        <Route path="loans/:loanId" element={<LoanDetail />} />
        <Route path="expenditures" element={<Expenditures />} />
        <Route path="expenditure-masters" element={<ExpenditureMasters />} />
        <Route path="resource-persons" element={<ResourcePersons />} />
        <Route path="funds" element={<Funds />} />
        <Route path="assets" element={<Assets />} />
        <Route path="reports" element={<Reports />} />
        <Route path="reports/statistics" element={<StatisticsReport />} />
        <Route path="reports/credit-history" element={<CreditHistorySearch />} />
        <Route path="reports/cashbook" element={<CashbookReport />} />
        <Route path="reports/credit-report" element={<CreditReportPage />} />
        <Route path="reports/loan-emi" element={<LoanEmiReport />} />
        <Route path="reports/loan-ledger" element={<LoanLedgerReport />} />
        <Route path="reports/dcb" element={<DcbReport />} />
        <Route path="reports/par" element={<ParReport />} />
        <Route path="reports/repayment-npa" element={<RepaymentNpaReport />} />
        <Route path="reports/crp-ep" element={<CrpEpReport />} />
        <Route path="reports/asset-register" element={<AssetRegisterReport />} />
        <Route path="reports/branch-comparison" element={<BranchComparisonReport />} />
        <Route path="reports/grounded-enterprises" element={<GroundedEnterprisesReport />} />
        <Route path="reports/loan-certificates" element={<LoanCertificatesReport />} />
        <Route path="reports/financial-summary" element={<FinancialSummaryReport />} />
        <Route path="reports/livelihoods" element={<LivelihoodsReport />} />
        <Route path="reports/member-ledger" element={<MemberLedgerReport />} />
        <Route path="reports/all-entrepreneurs" element={<AllEntrepreneursReport />} />
        <Route path="reports/project-expenditure" element={<ProjectExpenditureReport />} />
        <Route path="business-plans" element={<BusinessPlans />} />
        <Route path="business-plans/:id" element={<BusinessPlanForm />} />
        <Route path="reports/business-plans" element={<BusinessPlanReport />} />
        <Route path="reports/loan-applications" element={<LoanApplicationReport />} />
        <Route path="cash-transfers" element={<CashTransfers />} />
        <Route path="activity-logs" element={<ActivityLogs />} />
        <Route path="backup-restore" element={<BackupRestore />} />
        <Route path="requisitions" element={<Requisitions />} />
        <Route path="financial-years" element={<FinancialYears />} />
        <Route path="livelihoods" element={<Livelihoods />} />
        <Route path="notices" element={<Notices />} />
        <Route path="member-ledger" element={<AdminOnly><MemberLedger /></AdminOnly>} />
        <Route path="search" element={<SearchPage />} />
      </Route>
    </Routes>
  );
}

function App() {
  React.useEffect(() => {
    document.title = 'WWAGS MIS';
    // Re-assert after any external script tries to override
    const interval = setInterval(() => {
      if (document.title !== 'WWAGS MIS') document.title = 'WWAGS MIS';
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster
          position="top-center"
          offset="40vh"
          toastOptions={{
            duration: 2200,
            className: '!bg-emerald-50 !border-2 !border-emerald-300 !text-emerald-900 !font-bold !text-base !shadow-2xl !min-w-[280px] !justify-center',
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
